// The Simulator's thread, as data (owner, 2026-10-01: « use the same simulator
// as the conversation — same everything: if they send images and videos I need
// to see them and play the videos, I need to be able to send images too, see
// them, delete them — like the Conversations WhatsApp view we have now »).
//
// The thread is DRAWN by the Conversations panel's own components
// (chat/ChatThread.tsx); this file only turns the simulator's turns into the
// messages they draw, into what /simulate reads, and folds in what it answers.
//
// The thread lives in the browser (localStorage) and is posted whole each
// turn; files go to the panel's private bucket first and are named by path.
// Nothing here is a real conversation, and nothing is sent to WhatsApp.
//
// Guard: ./simulatorThread.test.ts
import type { SimulateMessage, SimulateResult, WhatsAppMessage } from '@/lib/api';
import { kindForFile } from './chat/threadFormat';

export type SimFileKind = 'image' | 'video' | 'voice' | 'document';

export interface SimFile {
  /** Where the panel bucket keeps it (`sim/<person>/…`) — what the simulated
   *  customer sent. The agent's Brain files have no path: shown, never re-read. */
  path?: string;
  /** A signed link; refreshed by path while the thread is open. */
  url: string;
  /** When `url` was signed (ms). Links last an hour. */
  signedAt?: number;
  kind: SimFileKind;
  mime: string;
  filename?: string | null;
}

/** A link this old is treated as dead: an hour's life, ten minutes' margin. */
const LINK_LIFE_MS = 50 * 60_000;

export interface SimTurn {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** When it was sent — the time and the day separator under each bubble. */
  at: string;
  file?: SimFile | null;
  /** What a voice note said, as /simulate heard it. */
  transcript?: string | null;
  /** Why nobody could hear it (the owner's « as if it heard nothing », said). */
  transcriptError?: string | null;
  /** Deleted in the simulator: drawn « Message supprimé », never read by the agent again. */
  deletedAt?: string | null;
  // ── the agent's reply ──
  needsHuman?: boolean;
  reason?: string;
  /** false = this reply saw none of the customer's state. Shown, never assumed. */
  factsUsed?: boolean;
  /** The agent answers this outright — no model call. */
  decidedWithoutModel?: boolean;
  /** A file the reply carried after its first: its own message on her phone,
   *  no words — the id of the reply it belongs to. */
  followsReply?: string;
}

export function newTurnId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID ? c.randomUUID() : `sim-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** A recorded or attached sound is a voice note — heard like hers; anything else goes as the paperclip sends it. */
export function simKindForFile(file: { type: string }): SimFileKind {
  return /^audio\//i.test(file.type) ? 'voice' : kindForFile(file);
}

/** The thread as /simulate reads it: what is still in the conversation, files by path. */
export function toPayload(turns: SimTurn[]): SimulateMessage[] {
  return turns
    // Deleted: no longer on her phone, so not in what the agent reads — as
    // live (buildConversation skips a message deleted for everyone). A file
    // the reply carried after its first has no words to read back.
    .filter((t) => !t.deletedAt && !t.followsReply)
    .filter((t) => (t.role === 'user' ? Boolean(t.content.trim() || t.file?.path) : Boolean(t.content.trim())))
    .map((t) => ({
      id: t.id,
      role: t.role,
      content: t.content,
      ...(t.role === 'user' && t.file?.path
        ? { media: { path: t.file.path, kind: t.file.kind, mime: t.file.mime, filename: t.file.filename ?? null } }
        : {}),
      // Heard once, never paid for twice.
      ...(t.role === 'user' && t.transcript ? { transcript: t.transcript } : {}),
    }));
}

/** The paths whose links to keep fresh: her files still in the thread. */
export function mediaPaths(turns: SimTurn[]): string[] {
  return [...new Set(turns.filter((t) => !t.deletedAt && t.file?.path).map((t) => t.file!.path!))];
}

/** The messages the Conversations thread draws — the same shape the worker gives it for a real chat. */
export function toMessages(turns: SimTurn[], freshUrls: Record<string, string>, noReplyText: string, now: number = Date.now()): WhatsAppMessage[] {
  return turns.map((t) => {
    const ours = t.role === 'assistant';
    // ⚠️ A DEAD LINK IS NEVER DRAWN. The thread outlives its links (an hour):
    // drawn, an expired one turns the panel's picture into a download chip for
    // good and leaves a voice note silent. Her files are re-signed by path, so
    // until the fresh link comes, nothing. The agent's Brain files cannot be
    // re-signed here, so theirs stays.
    const aged = Boolean(t.file?.path) && t.file?.signedAt !== undefined && now - t.file.signedAt > LINK_LIFE_MS;
    const url = t.file ? ((t.file.path && freshUrls[t.file.path]) || (aged ? null : t.file.url) || null) : null;
    const body = t.content || (ours && !t.file ? noReplyText : '');
    return {
      id: t.id,
      wa_message_id: t.id,
      body: body || null,
      direction: ours ? 'out' : 'in',
      push_name: null,
      has_media: Boolean(t.file),
      media_mimetype: t.file?.mime ?? null,
      media_filename: t.file?.filename ?? null,
      media_url: url,
      wa_timestamp: t.at,
      created_at: t.at,
      sender: ours ? 'agent' : 'client',
      kind: t.file ? t.file.kind : 'text',
      // Nothing to quote and no ticks: nothing here went through WhatsApp.
      quotable_id: null,
      status: null,
      transcript: t.transcript ?? null,
      transcript_error: t.transcriptError ?? null,
      quoted_body: null,
      delete_until: null,
      delete_state: t.deletedAt ? 'deleted' : null,
      deleted_at: t.deletedAt ?? null,
      delete_error: null,
    };
  });
}

const REPLY_MIME: Record<'image' | 'video' | 'document', string> = {
  image: 'image/jpeg', video: 'video/mp4', document: 'application/octet-stream',
};

/** What /simulate answered, folded in: each voice note's words (or why not), then the reply. */
export function applyResult(turns: SimTurn[], res: SimulateResult, at: string): SimTurn[] {
  const heard = new Map((res.voice ?? []).map((v) => [v.id, v]));
  const updated = turns.map((t) => {
    const v = heard.get(t.id);
    return v ? { ...t, transcript: v.transcript ?? null, transcriptError: v.transcript ? null : (v.error ?? null) } : t;
  });
  // As her phone gets it (whatsapp-relay/src/outbox.js): one message per
  // file, the words under the first.
  const files = (res.reply_media ?? []).map((f) => ({ url: f.url, kind: f.kind, mime: REPLY_MIME[f.kind] ?? REPLY_MIME.document }));
  const reply: SimTurn = {
    id: newTurnId(),
    role: 'assistant',
    content: res.reply ?? '',
    at,
    file: files[0] ?? null,
    needsHuman: res.needs_human,
    reason: res.reason || undefined,
    factsUsed: res.facts_used,
    decidedWithoutModel: res.decided_without_model,
  };
  const more: SimTurn[] = files.slice(1).map((file) => ({
    id: newTurnId(), role: 'assistant', content: '', at, file, followsReply: reply.id,
  }));
  return [...updated, reply, ...more];
}

const FILE_WORD: Record<SimFileKind, string> = { image: '[photo]', video: '[vidéo]', voice: '[note vocale]', document: '[fichier]' };

/** The conversation before a reply, for « corriger »: in words, a file described. */
export function teachMessages(turns: SimTurn[], uptoId: string): Array<{ role: 'user' | 'assistant'; content: string }> {
  const end = turns.findIndex((t) => t.id === uptoId);
  return turns.slice(0, end < 0 ? turns.length : end)
    .filter((t) => !t.deletedAt && !t.followsReply)
    .map((t) => {
      const words = t.content.trim();
      if (t.file?.kind === 'voice') return { role: t.role, content: t.transcript ? `${FILE_WORD.voice} ${t.transcript}` : FILE_WORD.voice };
      return { role: t.role, content: words || (t.file ? FILE_WORD[t.file.kind] : '') };
    })
    .filter((m) => m.content);
}

/**
 * The saved thread. An older one (before 2026-10-01) carried its files inline
 * — base64 images and audio, never uploaded — so its words are kept and its
 * files dropped. Unreadable → an empty thread, never a crash.
 */
export function restoreTurns(raw: string | null, now: Date = new Date()): SimTurn[] {
  let parsed: unknown;
  try { parsed = raw ? JSON.parse(raw) : null; } catch { return []; }
  if (!Array.isArray(parsed)) return [];
  const out: SimTurn[] = [];
  for (const item of parsed) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    if (typeof o.id === 'string' && typeof o.at === 'string') {
      // Ours, but never trusted blindly: a turn with no words must not break
      // the tab on its first `.trim()`.
      out.push({
        ...(o as unknown as SimTurn),
        role: o.role === 'assistant' ? 'assistant' : 'user',
        content: typeof o.content === 'string' ? o.content : '',
      });
      continue;
    }
    const content = typeof o.content === 'string' ? o.content : '';
    if (!content.trim()) continue;
    out.push({
      id: newTurnId(),
      role: o.role === 'assistant' ? 'assistant' : 'user',
      content,
      at: now.toISOString(),
      ...(typeof o.needsHuman === 'boolean' ? { needsHuman: o.needsHuman } : {}),
      ...(typeof o.reason === 'string' ? { reason: o.reason } : {}),
      ...(typeof o.factsUsed === 'boolean' ? { factsUsed: o.factsUsed } : {}),
      ...(typeof o.decidedWithoutModel === 'boolean' ? { decidedWithoutModel: o.decidedWithoutModel } : {}),
    });
  }
  return out;
}
